import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Heart, Users, Rocket, MapPin, Clock } from 'lucide-react';

const Careers = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '600px',
    margin: '0 auto'
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px'
  };

  const cardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const listStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '2',
    paddingLeft: '20px'
  };

  const valuesGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '24px'
  };

  const valueCardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const iconWrapperStyle = {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: themeColors.primary + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  };

  const buttonStyle = {
    backgroundColor: themeColors.primary,
    color: '#ffffff',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    marginTop: '16px',
    transition: 'opacity 0.2s'
  };

  const positions = [
    {
      title: 'Full-Stack Developer',
      location: 'Remote / Seattle, WA',
      type: 'Full-time',
      description: 'Help build and scale our React + Node.js marketplace platform. Experience with MongoDB, Stripe, and cloud deployment preferred.'
    },
    {
      title: 'UI/UX Designer',
      location: 'Remote',
      type: 'Contract',
      description: 'Design beautiful, intuitive experiences for parents buying and selling baby items. Mobile-first design experience required.'
    },
    {
      title: 'Community Manager',
      location: 'Remote',
      type: 'Part-time',
      description: 'Grow and nurture our parent community. Create content, moderate discussions, and gather user feedback.'
    },
    {
      title: 'Marketing Intern',
      location: 'Remote',
      type: 'Internship',
      description: 'Help spread the word about BabyResell through social media, content creation, and grassroots marketing campaigns.'
    }
  ];

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Join Our Team</h1>
          <p style={subtitleStyle}>
            Help us build the future of sustainable parenting. At BabyResell, we're on a mission to make quality baby items accessible to every family.
          </p>
        </div>

        {/* Why Work at BabyResell */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Why Work at BabyResell</h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              BabyResell is a product of Interverse, and we're building something meaningful — a platform that helps families save money while reducing waste. We're a small, passionate team looking for people who want to make a real difference.
            </p>
          </div>
        </div>

        {/* Our Values */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Our Values</h2>
          <div style={valuesGridStyle}>
            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Heart size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Mission-Driven
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                Everything we build serves one purpose: helping families. We measure success by the impact we create, not just metrics.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Users size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Community First
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                We're parents too. We understand the challenges families face and build solutions with empathy and care.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Rocket size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Move Fast, Stay Thoughtful
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                We ship quickly but never cut corners on safety, quality, or user experience.
              </p>
            </div>
          </div>
        </div>

        {/* Open Positions */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Open Positions</h2>
          {positions.map((position, index) => (
            <div key={index} style={cardStyle}>
              <h3 style={{ color: themeColors.text, fontSize: '22px', fontWeight: 'bold', marginBottom: '12px' }}>
                {position.title}
              </h3>
              <div style={{ display: 'flex', gap: '20px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: themeColors.textSecondary }}>
                  <MapPin size={16} />
                  <span style={{ fontSize: '14px' }}>{position.location}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: themeColors.textSecondary }}>
                  <Clock size={16} />
                  <span style={{ fontSize: '14px' }}>{position.type}</span>
                </div>
              </div>
              <p style={paragraphStyle}>{position.description}</p>
              <a
                href={`mailto:careers@interverse.app?subject=Application: ${position.title}`}
                style={buttonStyle}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                Apply Now
              </a>
            </div>
          ))}
        </div>

        {/* Don't See Your Role? */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Don't See Your Role?</h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              We're always looking for talented people who share our vision. Send your resume and tell us how you'd like to contribute.
            </p>
            <a
              href="mailto:careers@interverse.app?subject=General Application"
              style={buttonStyle}
              onMouseOver={(e) => e.target.style.opacity = '0.9'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
            >
              Send Us Your Resume
            </a>
          </div>
        </div>

        {/* Benefits & Perks */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Benefits & Perks</h2>
          <div style={cardStyle}>
            <ul style={listStyle}>
              <li>Remote-first culture — work from anywhere</li>
              <li>Flexible hours that respect work-life balance</li>
              <li>Equity options for full-time team members</li>
              <li>Direct impact on a growing platform used by real families</li>
              <li>Collaborative, supportive team environment</li>
              <li>Professional development budget</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
